import { ChangeDetectionStrategy, Component, computed, input } from '@angular/core';

import {
  type DynamicTextConfig,
  FONT_FAMILY_MAP,
  type FontFamily,
  FONT_WEIGHT_MAP,
  type FontWeight,
  type TextTag,
  TEXT_SIZE_MAP,
} from './dynamic-text.types';

@Component({
  selector: 'app-dynamic-text',
  templateUrl: './dynamic-text.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class DynamicText {
  readonly config = input.required<DynamicTextConfig>();

  protected readonly tag = computed<TextTag>(() => this.config().tag || 'p');
  protected readonly text = computed(() => this.config().text);
  protected readonly size = computed(() => TEXT_SIZE_MAP[this.config().size || 'sm']);

  protected readonly fontWeight = computed(() => {
    const override = this.config().fontWeight;
    const resolved = override ? (FONT_WEIGHT_MAP[override as FontWeight] ?? override) : this.size().fontWeight;
    return this.wrapWithPrefix('font-weight', resolved);
  });

  protected readonly color = computed(() => {
    const resolved = this.config().color || 'var(--body-color)';
    return this.wrapWithPrefix('color', resolved);
  });

  protected readonly fontFamily = computed(() => {
    const override = this.config().fontFamily;
    const resolved = override ? (FONT_FAMILY_MAP[override as FontFamily] ?? override) : FONT_FAMILY_MAP['sans'];
    return this.wrapWithPrefix('font-family', resolved);
  });

  protected readonly fontSize = computed(() => {
    return this.wrapWithPrefix('font-size', this.size().fontSize);
  });

  protected readonly lineHeight = computed(() => {
    const resolved = this.config().lineHeight ?? this.size().lineHeight;
    return this.wrapWithPrefix('line-height', resolved);
  });

  protected readonly letterSpacing = computed(() => {
    const resolved = this.config().letterSpacing ?? this.size().letterSpacing;
    return this.wrapWithPrefix('letter-spacing', resolved);
  });

  protected readonly alignment = computed(() => this.config().alignment || 'left');
  protected readonly gradient = computed(() => {
    const g = this.config().gradient;
    return g ? `gradient-${g}` : undefined;
  });
  protected readonly whiteSpace = computed(() => this.config().whiteSpace);
  protected readonly textWrap = computed(() => this.config().textWrap);

  private wrapWithPrefix(prop: string, value: string): string {
    const prefix = this.config().cssVarPrefix;
    return prefix ? `var(--${prefix}-${prop}, ${value})` : value;
  }
}
